import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, Upload, Icon, Steps, Typography, Divider, Alert, Spin, message, Button, Statistic, Row, Col } from 'antd';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

import { processSheet } from './utils/smartImportPipeline';
import { fetchMasterData, emptyMasters } from './utils/referenceResolver';
import { getLearnedAliases, learnAlias } from './utils/importDictionaryDB';
import { normalizeKey } from './utils/textNormalize';
import { generateLocationCode, generateAssetSubtypeCode } from './utils/assetCodeGen';
import { buildAssetModelName } from './utils/assetModelName';
import { buildImportCsv, buildUnmatchedReportCsv, downloadText } from './utils/importOutputWriter';
import ColumnMappingReview from './components/smartimport/ColumnMappingReview';
import ReviewQueue from './components/smartimport/ReviewQueue';
import AuditLogPanel from './components/smartimport/AuditLogPanel';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Step } = Steps;

// Pick the sheet with the most rows (client files often have an empty trailing "Sheet1").
const pickBestSheet = (workbook) => {
  let best = { name: workbook.SheetNames[0], rows: -1 };
  workbook.SheetNames.forEach((name) => {
    const grid = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, defval: null });
    if (grid.length > best.rows) best = { name, rows: grid.length };
  });
  return best.name;
};

// Recompute auto-generated codes after any user edits to the source-of-truth columns.
const applyEditsAndCodes = (row, edits) => {
  const output = { ...row.output, ...(edits || {}) };
  // Location codes are always derived from the (possibly edited) location names.
  output['Location Code'] = generateLocationCode(output[' Location']);
  output['Sub Location Code'] = generateLocationCode(output['Sub Location']);
  // Asset Subtype Code: keep a mapped-from-source or user-edited value; only regenerate from
  // the Asset Sub Type when it was neither mapped nor edited.
  const subtypeEdited = edits && edits['Asset Subtype Code'] !== undefined;
  if (!(row.extras || {}).subtypeCodeMapped && !subtypeEdited) {
    output['Asset Subtype Code'] = generateAssetSubtypeCode(output['Asset Sub Type']);
  }
  // Model is auto-created from Make + Asset Sub Type + Model No (optional) — recompute from
  // the (possibly edited) Make / Sub Type so the export matches what the review grid showed.
  output['Model'] = buildAssetModelName(output['Make'], output['Asset Sub Type'], (row.extras || {}).modelNo);
  return { ...row, output };
};

function SmartAssetImport() {
  const [step, setStep] = useState(0); // 0 upload, 1 mapping, 2 review, 3 done
  const [fileName, setFileName] = useState('');
  const [grid, setGrid] = useState(null);
  const [parsing, setParsing] = useState(false);

  const [masters, setMasters] = useState(emptyMasters());
  const [mastersLoading, setMastersLoading] = useState(false);
  const [masterError, setMasterError] = useState('');

  const [learnedAliases, setLearnedAliases] = useState({});
  const [mappingOverride, setMappingOverride] = useState({});
  const [rowEdits, setRowEdits] = useState({});
  const [includes, setIncludes] = useState({});

  useEffect(() => { setLearnedAliases(getLearnedAliases()); }, []);

  // Fetch AssetPro master data once a file is loaded (used for exact-match validation).
  const loadMasters = useCallback(async () => {
    setMastersLoading(true);
    setMasterError('');
    const md = await fetchMasterData();
    setMasters(md);
    if (!md.available) {
      setMasterError(md.error
        ? `Could not load AssetPro master data: ${md.error}. Values can't be checked as existing-vs-new, so all rows will be flagged for review.`
        : 'AssetPro master data came back empty. Values will be flagged as new and routed to review.');
    }
    setMastersLoading(false);
  }, []);

  // The whole deterministic pipeline — recomputed when inputs change. No network here.
  const result = useMemo(() => {
    if (!grid) return null;
    return processSheet(grid, { masters, learnedAliases, mappingOverride });
  }, [grid, masters, learnedAliases, mappingOverride]);

  const handleFileUpload = (file) => {
    setParsing(true);
    const reader = new FileReader();
    const name = file.name.toLowerCase();
    reader.onload = (e) => {
      try {
        let parsedGrid = null;
        if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
          const workbook = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
          const sheetName = pickBestSheet(workbook);
          parsedGrid = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: null });
        } else if (name.endsWith('.csv')) {
          const res = Papa.parse(e.target.result, { header: false, skipEmptyLines: false });
          parsedGrid = res.data;
        } else {
          throw new Error('Unsupported file type. Upload .xlsx, .xls, or .csv');
        }
        if (!parsedGrid || parsedGrid.length === 0) throw new Error('File appears to be empty');

        setFileName(file.name);
        setGrid(parsedGrid);
        setMappingOverride({});
        setRowEdits({});
        setIncludes({});
        setStep(1);
        loadMasters();
        message.success(`Loaded ${file.name}`);
      } catch (err) {
        message.error(`Error loading file: ${err.message}`);
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => { message.error('Failed to read file'); setParsing(false); };
    if (name.endsWith('.csv')) reader.readAsText(file); else reader.readAsBinaryString(file);
    return false; // prevent antd auto-upload
  };

  const handleSetOverride = (fieldKey, colIndex) => {
    setMappingOverride((prev) => {
      const next = { ...prev };
      if (colIndex === null || colIndex === undefined) {
        // Unmap: also block the alias-detected mapping by setting to -1 sentinel.
        next[fieldKey] = -1;
      } else {
        next[fieldKey] = colIndex;
      }
      return next;
    });
  };

  // Confirm mapping -> learn any manual assignments so they auto-map next time.
  const confirmMapping = () => {
    if (!result) return;
    Object.keys(mappingOverride).forEach((fieldKey) => {
      const colIndex = mappingOverride[fieldKey];
      if (colIndex >= 0) {
        const header = (result.profile.headers.find((h) => h.index === colIndex) || {}).raw;
        const key = normalizeKey(header);
        if (key) { learnAlias(key, fieldKey); }
      }
    });
    setLearnedAliases(getLearnedAliases());

    // Initialize include defaults: everything except BLOCKED rows.
    const init = {};
    result.rows.forEach((r) => { init[r.rowId] = r.status !== 'BLOCKED'; });
    setIncludes(init);
    setStep(2);
  };

  const onEdit = (rowId, column, value) => {
    setRowEdits((prev) => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), [column]: value } }));
  };
  const onToggleInclude = (rowId, val) => setIncludes((prev) => ({ ...prev, [rowId]: val }));

  // Bulk-fill a whole column with the same value, or an incrementing one (e.g. invoice numbers).
  // Preserves the numeric width/prefix of the start value: "INV-001" -> INV-001, INV-002, …
  const incrementValue = (start, i) => {
    const s = start == null ? '' : String(start);
    const m = s.match(/^(.*?)(\d+)(\D*)$/);
    if (m) return `${m[1]}${String(parseInt(m[2], 10) + i).padStart(m[2].length, '0')}${m[3]}`;
    return `${s}${i + 1}`;
  };
  const onBulkFill = (column, opts) => {
    if (!result) return;
    const { mode, value = '', prefix = '', suffix = '', blanksOnly } = opts || {};
    setRowEdits((prev) => {
      const next = { ...prev };
      let counter = 0;
      result.rows.forEach((r) => {
        const cur = next[r.rowId] && next[r.rowId][column] !== undefined ? next[r.rowId][column] : r.output[column];
        const curStr = cur === null || cur === undefined ? '' : String(cur);
        const isBlank = curStr.trim() === '';
        let v;
        if (mode === 'affix') {
          if (isBlank) return; // nothing to wrap
          v = `${prefix}${curStr}${suffix}`;
        } else {
          if (blanksOnly && !isBlank) return;
          v = mode === 'incremental' ? incrementValue(value, counter) : value;
          counter += 1;
        }
        next[r.rowId] = { ...(next[r.rowId] || {}), [column]: v };
      });
      return next;
    });
    message.success(`Updated "${column.trim()}"`);
  };
  const onBulkInclude = (statusFilter, val) => {
    if (!result) return;
    setIncludes((prev) => {
      const next = { ...prev };
      result.rows.forEach((r) => {
        if (statusFilter === 'ALL' || r.status === statusFilter) next[r.rowId] = val;
      });
      return next;
    });
  };

  const exportFiles = () => {
    if (!result) return;
    const included = result.rows
      .filter((r) => includes[r.rowId])
      .map((r) => applyEditsAndCodes(r, rowEdits[r.rowId]));
    if (included.length === 0) { message.warning('No rows selected'); return; }

    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadText(`assetpro-import-${stamp}.csv`, buildImportCsv(included));
    downloadText(`assetpro-import-auditlog-${stamp}.csv`, result.transformLog.toCSV());
    const unmatched = buildUnmatchedReportCsv(result.rows.filter((r) => includes[r.rowId]));
    downloadText(`assetpro-import-unmatched-${stamp}.csv`, unmatched);

    message.success(`Exported ${included.length} rows (+ audit log and unmatched report)`);
    setStep(3);
  };

  const reset = () => {
    setStep(0); setGrid(null); setFileName(''); setMappingOverride({});
    setRowEdits({}); setIncludes({}); setMasters(emptyMasters()); setMasterError('');
  };

  const summary = result ? result.summary : null;

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Smart Asset Import</Title>
      <Text type="secondary">
        Upload an existing asset file. The system auto-detects its structure, maps columns to AssetPro fields,
        checks each value against AssetPro master data by exact match, and prepares an import-ready file —
        flagging anything uncertain for your review. No fuzzy guessing.
      </Text>
      <Divider />

      <Steps current={step} size="small" style={{ marginBottom: 24 }}>
        <Step title="Upload" />
        <Step title="Column mapping" />
        <Step title="Review & confirm" />
        <Step title="Export" />
      </Steps>

      {step === 0 && (
        <Card>
          <Dragger accept=".xlsx,.xls,.csv" beforeUpload={handleFileUpload} showUploadList={false} disabled={parsing}>
            <p className="ant-upload-drag-icon"><Icon type="inbox" /></p>
            <p className="ant-upload-text">Click or drag an asset file to upload</p>
            <p className="ant-upload-hint">Excel (.xlsx, .xls) or CSV. Headers don't need to be on row 1.</p>
          </Dragger>
          {parsing && <div style={{ textAlign: 'center', marginTop: 16 }}><Spin /> Parsing file…</div>}
        </Card>
      )}

      {step >= 1 && result && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={4}><Statistic title="Rows" value={summary.totalRows} /></Col>
            <Col span={4}><Statistic title="Auto-accept" value={summary.autoAccept} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col span={4}><Statistic title="Needs review" value={summary.review} valueStyle={{ color: '#fa8c16' }} /></Col>
            <Col span={4}><Statistic title="Blocked" value={summary.blocked} valueStyle={{ color: '#f5222d' }} /></Col>
            <Col span={4}><Statistic title="New to AssetPro" value={summary.unmatchedMaster} /></Col>
            <Col span={4}><Statistic title="Duplicates" value={summary.duplicates} /></Col>
          </Row>
          <Text type="secondary">File: {fileName} · header row {summary.headerRowIndex + 1}</Text>
          {mastersLoading && <div style={{ marginTop: 8 }}><Spin size="small" /> <Text type="secondary">Loading AssetPro master data…</Text></div>}
          {masterError && <Alert type="warning" showIcon style={{ marginTop: 8 }} message={masterError} />}
        </Card>
      )}

      {step === 1 && result && (
        <ColumnMappingReview
          headers={result.profile.headers}
          mapping={result.columnMap.mapping}
          unmappedSourceColumns={result.columnMap.unmappedSourceColumns}
          unmappedRequiredFields={result.columnMap.unmappedRequiredFields}
          headerAmbiguous={result.profile.headerAmbiguous}
          headerRowIndex={result.profile.headerRowIndex}
          droppedEmptyColumns={result.profile.droppedEmptyColumns}
          onSetOverride={handleSetOverride}
          onBack={reset}
          onConfirm={confirmMapping}
        />
      )}

      {step === 2 && result && (
        <div>
          <ReviewQueue
            rows={result.rows}
            masters={masters}
            rowEdits={rowEdits}
            includes={includes}
            onEdit={onEdit}
            onToggleInclude={onToggleInclude}
            onBulkInclude={onBulkInclude}
            onBulkFill={onBulkFill}
            onBack={() => setStep(1)}
            onExport={exportFiles}
          />
          <Divider />
          <AuditLogPanel
            entries={result.transformLog.entries}
            onDownloadCsv={() => downloadText('audit-log.csv', result.transformLog.toCSV())}
            onDownloadJson={() => downloadText('audit-log.json', result.transformLog.toJSON(), 'application/json')}
          />
        </div>
      )}

      {step === 3 && (
        <Card>
          <Alert
            type="success"
            showIcon
            message="Import file ready"
            description="Downloaded: the AssetPro import CSV, the transformation audit log, and an unmatched-records report. Review the unmatched report to create any missing master records in AssetPro before importing."
            style={{ marginBottom: 16 }}
          />
          <Button onClick={reset}>Import another file</Button>
        </Card>
      )}
    </div>
  );
}

export default SmartAssetImport;
